import type { Directive, DirectiveBinding } from "vue";

/**
 * v-arrow-scroll
 * -------------------------------------------------------------------------
 * Macht einen scrollbaren Container per Tastatur bedienbar (Issue #642):
 * Pfeiltasten, Bild auf/ab, Pos1/Ende und Leertaste scrollen den Bereich –
 * ohne dass man vorher mit der Maus hineinklicken muss.
 *
 * Wichtig: Solange der Fokus in einem Eingabefeld liegt (input/textarea/
 * select/contenteditable), werden die Tasten NICHT abgefangen – so bleibt
 * das Editieren von Zahlen (z. B. im Werte-Dialog) unangetastet.
 *
 * Verwendung:
 *   <div class="scrollbereich" v-arrow-scroll>…</div>
 *   <v-card-text v-arrow-scroll.autofocus>…</v-card-text>   // fokussiert beim Öffnen
 *
 * Modifier:
 *   .autofocus  – legt den Fokus beim Einhängen auf den Container (z. B. in
 *                 Dialogen), damit Pfeiltasten sofort scrollen.
 *
 * Deaktivieren: v-arrow-scroll="false"
 */

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** Schritt pro Pfeiltasten-Druck (px). Bewusst zügig, damit Halten flüssig wirkt. */
const ARROW_STEP = 48;
/** Anteil der sichtbaren Höhe pro Bild-auf/ab bzw. Leertaste. */
const PAGE_RATIO = 0.9;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  // Vuetify-Kombiboxen etc. haben role=combobox/listbox mit eigener Tastensteuerung.
  const role = target.getAttribute("role");
  if (role === "combobox" || role === "listbox" || role === "textbox") return true;
  return false;
}

interface ArrowScrollEl extends HTMLElement {
  __arrowScrollHandler__?: (e: KeyboardEvent) => void;
}

function makeHandler(el: HTMLElement) {
  return (e: KeyboardEvent) => {
    // Modifier-Kombinationen (z. B. Cmd+Pfeil) dem Browser überlassen.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // Beim Tippen in Feldern nichts abfangen.
    if (isEditableTarget(e.target)) return;

    const canScrollY = el.scrollHeight > el.clientHeight + 1;
    const canScrollX = el.scrollWidth > el.clientWidth + 1;
    const page = Math.max(el.clientHeight * PAGE_RATIO, ARROW_STEP);

    let top = 0;
    let left = 0;

    switch (e.key) {
      case "ArrowDown":
        top = ARROW_STEP;
        break;
      case "ArrowUp":
        top = -ARROW_STEP;
        break;
      case "ArrowRight":
        if (!canScrollX) return;
        left = ARROW_STEP;
        break;
      case "ArrowLeft":
        if (!canScrollX) return;
        left = -ARROW_STEP;
        break;
      case "PageDown":
        top = page;
        break;
      case "PageUp":
        top = -page;
        break;
      case " ":
      case "Spacebar":
        top = e.shiftKey ? -page : page;
        break;
      case "Home":
        if (!canScrollY) return;
        el.scrollTo({ top: 0, behavior: "smooth" });
        e.preventDefault();
        return;
      case "End":
        if (!canScrollY) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        e.preventDefault();
        return;
      default:
        return;
    }

    // Vertikale Tasten nur bedienen, wenn es etwas zu scrollen gibt – sonst
    // die Standardaktion (z. B. Seiten-Scroll) nicht blockieren.
    if (top !== 0 && !canScrollY) return;

    el.scrollBy({ top, left, behavior: "auto" });
    e.preventDefault();
  };
}

const arrowScroll: Directive<ArrowScrollEl, unknown> = {
  mounted(el, binding: DirectiveBinding) {
    if (binding.value === false) return;

    // Fokussierbar machen, damit der Container Tastatur-Events erhält.
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
    if (!el.style.outline) el.style.outline = "none";

    const handler = makeHandler(el);
    el.addEventListener("keydown", handler);
    el.__arrowScrollHandler__ = handler;

    // In Dialogen den Fokus direkt auf den Scrollbereich legen, damit
    // Pfeiltasten ohne vorherigen Mausklick wirken.
    if (binding.modifiers.autofocus) {
      requestAnimationFrame(() => {
        // Nur greifen, wenn nicht ohnehin schon ein Feld im Bereich aktiv ist.
        const active = document.activeElement;
        if (active && el.contains(active) && isEditableTarget(active)) return;
        el.focus({ preventScroll: true });
      });
    }
  },
  unmounted(el) {
    if (el.__arrowScrollHandler__) {
      el.removeEventListener("keydown", el.__arrowScrollHandler__);
      delete el.__arrowScrollHandler__;
    }
  },
};

export default arrowScroll;
